<template>
  <div
    v-if="rv"
    class="max-lg:pb-[calc(120px+env(safe-area-inset-bottom))] lg:pb-0"
  >
    <TContainer class="relative lg:mt-8 lg:mb-12">
      <main class="grid gap-x-4 gap-y-8 [grid-template-areas:'mosaic'_'top'_'content'_'floating'] lg:[grid-template-areas:'top_top'_'mosaic_floating'_'content_floating'] lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-x-8">
        <RvRentalRvTitle class="[grid-area:top]" />

        <div class="[grid-area:mosaic] relative w-screen left-1/2 ml-[-50vw] lg:w-auto lg:ml-0 lg:left-auto lg:order-0">
          <LazyAppMosaicImages
            v-if="rvPhotos?.length"
            ref="mosaicImagesRef"
            :images="mosaicImages"
          >
            <template #tour>
              <LazyRvExplore3d
                v-if="rvTour3DUrl"
                :src="rvTour3DUrl"
                class="inline-block"
              />
            </template>
          </LazyAppMosaicImages>

          <LazyRvRentalAddPhotoImageOwner
            v-else-if="isRvOwner"
            :rv-id="rvId"
          />

          <div
            v-if="isMasquerading"
            class="absolute bottom-4 left-4 lg:left-auto lg:right-4"
          >
            <RvReportPhotos
              :rv-number="rvRVNumber"
              :photos="rvPhotos"
              :listing-url="absoluteRvUrl"
            />
          </div>
        </div>

        <div
          class="flex items-center gap-2 justify-end max-lg:absolute max-lg:z-10 max-lg:top-0 max-lg:left-[-50dvw] max-lg:right-0 max-lg:w-screen max-lg:p-4 max-lg:ml-[50%] lg:ml-auto lg:[grid-area:top]"
         
        >
          <RvRentalBackButton
            v-if="!isWebView"
            appearance="card"
            class="mr-auto lg:hidden"
           
          />
          <RvRentalShareButton
            v-if="mosaicImages.length > 0 && rvIsPublished"
            page="rv-listing"
            outline
            compact
          />
          <FavouritesButton
            v-if="mosaicImages.length > 0 && rvIsPublished"
            :rv-id="rvId"
            has-label
            compact
          />
        </div>

        <div class="[grid-area:content] lg:order-0 flex flex-col divide-y divide-gray-200">
          <LazyRvRentalDetailsSection
            v-if="detailsSection.length"
            :options="detailsSection"
            class="py-8"
          />

          <RvRentalAboutSection class="py-8" />

          <LazyRvRentalAccommodations
            v-if="rvHasAccommodations"
            :accommodations="accommodationsOptions"
            class="py-8"
          />

          <LazyRvRentalAmenitiesSection
            v-if="rvInteriorFeatures.length"
            class="py-8"
          />

          <LazyRvRentalAmenitiesSection
            v-if="rvParkAmenities?.length"
            is-park
            class="py-8"
          />

          <LazyRvRentalDeliverySection
            v-if="!rvIsStatic && detailsSection.includes('delivery')"
            class="py-8"
          />

          <LazyRvRentalKoaCampgrounds
            v-if="!rvIsStatic && rvLocation?.Latitude && rvLocation?.Longitude"
            :view-all-link="viewAllCampgroundsLink"
            class="py-8"
          />

          <RvRentalRatesSection class="py-8" />

          <LazyRvRentalAddonsSection
            v-if="rvHasAddons"
            class="py-8"
          />

          <RvRentalRulesSection class="py-8" />

          <RvRentalCancellationPolicySection class="py-8" />

          <RvRentalAvailabilitySection
            v-if="rvIsPublished"
            class="py-8"
          />

          <RvRentalOwnerSection class="py-8" />

          <LazyRvRentalReviewsSection
            v-if="rvReviews?.length"
            id="reviews"
            class="py-8"
          />

          <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
            <template #enabled>
              <GeneralBrandPillars
                focus="guest"
                class="py-8"
              />
            </template>
          </ExperimentComponent>
        </div>

        <div class="[grid-area:floating] fixed bottom-0 left-0 right-0 z-40 lg:order-0 lg:bottom-auto lg:left-auto lg:right-auto lg:z-auto lg:relative">
          <div class="lg:sticky lg:top-30">
            <AtomTransition
              v-if="computedDiscountLabel && canShowDiscountTypeBadge"
              timing="fast"
            >
              <div
                v-show="canShowRangeDiscount || isLgBreakpoint"
                class="mb-2 shadow-xl mx-4 justify-self-center lg:mb-4 lg:shadow-none lg:mx-0 lg:justify-self-stretch"
              >
                <TTipbox variant="highlight">
                  <template #icon>
                    <TIcon
                      :icon="['far', 'tags']"
                      size="lg"
                    />
                  </template>
                  <span class="font-semibold">{{ computedDiscountLabel }}</span>
                </TTipbox>
              </div>
            </AtomTransition>

            <div>
              <RequestToBookPriceDetailsMobileTrigger @expand-details="expandDetails">
                <template #button>
                  <EstimateButton
                    is-destination-required
                    @open-breakdown="expandDetails"
                    @close-breakdown="closeDetails"
                  />
                </template>
              </RequestToBookPriceDetailsMobileTrigger>
            </div>

            <MoleculeScreenTakeover
              :takeover="expandedDetails && !isLgBreakpoint"
              :class="{ 'max-lg:hidden': !expandedDetails }"
              @update:takeover="toggleDetails"
            >
              <template #default="{ isTakeoverVisible }">
                <div class="min-h-full flex flex-col lg:p-5 lg:bg-white lg:shadow-sm lg:outline lg:outline-gray-200 lg:rounded-lg">
                  <EstimateListingForm
                    ref="estimateListingForm"
                    page-source="Listing Page"
                  >
                    <template #header>
                      <ExperimentComponent experiment-key="rvz28661SimplifiedRtbFlow">
                        <template #control>
                          <RvCardHeader class="mb-4 lg:mb-2 lg:hidden" />

                          <EstimateNightlyRate
                            :financial="bookingEstimateData?.Financial"
                            show-discount-badge
                            class="mb-2"
                          />
                        </template>

                        <template #variant>
                          <RvSummary
                            :name="rvRVName ?? ''"
                            :rating="rvRenterRating"
                            :number-of-reviews="rvReviews?.length"
                            :meta="`${t(rvLocalizedRVType)} ${t('in')} ${rvLocation?.City}, ${rvLocation?.State}`"
                            :image="rvMainPhoto"
                            class="lg:hidden border-b border-border-default pb-4 mb-4"
                          />

                          <EstimateNightlyRate
                            :financial="bookingEstimateData?.Financial"
                            show-discount-badge
                          />
                        </template>
                      </ExperimentComponent>
                    </template>
                  </EstimateListingForm>

                  <div
                    :class="[{
                      'max-lg:mt-auto': !isSimplifiedRtbFlowVariant,
                      'mt-4 lg:mt-5': isSimplifiedRtbFlowVariant,
                      'max-lg:sticky max-lg:bottom-0 max-lg:pt-4': isSimplifiedRtbFlowVariant && isTakeoverVisible,
                    }]"
                  >
                    <EstimateButton
                      is-destination-required
                      :class="{ 'shadow-[0px_0px_48px_16px_white]': isSimplifiedRtbFlowVariant && isTakeoverVisible }"
                      @open-breakdown="expandDetails"
                      @close-breakdown="closeDetails"
                    />

                    <ExperimentComponent experiment-key="rvz28661SimplifiedRtbFlow">
                      <template #control>
                        <p
                          v-if="!bookingEstimateData?.InstabookAvailable"
                          class="hidden lg:block mt-2 text-sm text-gray-500 text-center"
                        >
                          {{ t('booking.request.youWontBeCharged') }}
                        </p>
                      </template>

                      <template #variant>
                        <p
                          v-if="(rvIsStatic || !hasIncompleteDestination) && bookingEstimateData && !bookingEstimateData?.InstabookAvailable"
                          class="hidden lg:block mt-2 text-sm text-gray-500 text-center"
                        >
                          {{ t('booking.request.youWontBeCharged') }}
                        </p>
                      </template>
                    </ExperimentComponent>

                    <EstimateDisclaimerInternationalPricing class="hidden lg:block mt-2" />
                  </div>
                </div>
              </template>
            </MoleculeScreenTakeover>
          </div>
        </div>
      </main>
    </TContainer>

    <RvRentalSimilarRvsSection :destination="destinationData?.RouteDetails ?? undefined" />

    <RvRentalNearbyLocationsSection
      v-if="!isWebView && nearbyDestinations?.length"
      :destination="destinationData?.RouteDetails?.PointOfInterest ?? undefined"
      :locations="nearbyDestinations"
    />
  </div>
</template>

<script setup lang="ts">
import { COOKIES } from '#shared/constants/cookies'
import { ROUTE_BASE_NAME } from '~/constants'
import { BED_TYPES, DETAIL_TYPES } from '~/constants/rv'
import { RV_MANUFACTURERS_NOT_APPLICABLE, RV_MANUFACTURERS_OTHER } from '~/constants/manufacturers'

import type { COUNTRY_SHORTS } from '~/constants'
import type { RvUnpublishedError } from '~/types'
import type { StoredClickIds } from '~/types/click-ids'
import type { SwipeContent } from '~/components/_tailwind/swipe/types'

definePageMeta({
  middleware: ['minimal-layout', 'estimate'],

  i18n: {
    paths: {
      en: '/rv-rental/[alias]',
      fr: '/location-vr/[alias]',
    },
  },
})

const runtimeConfig = useRuntimeConfig()
const { isWebView } = usePlatform()
const route = useRoute()
const { locale, t } = useI18n()
const { isLgBreakpoint } = useBreakpoint()
const localePath = useLocalePath()
const { priceDisplay } = usePrice()
const { isMasquerading } = useMasquerade()
const { formatAsISOString } = useDates()

const { $rentals } = useNuxtApp()
const { data: rv, error } = await useAsyncData(
  computed(() => `rvDetails-${route.params.alias}`),
  () => $rentals(`/api/rvlistings/details`, {
    params: {
      name: route.params.alias as string,
      includeNegativeReviews: true,
    },
  }),
)

if ((error.value?.data as RvUnpublishedError)?.RVId) {
  // We want to return a 200 status code so it's a SOFT-404, but we want to make the page non-indexable.
  throw createError({
    status: 200,
    statusText: 'RV_UNPUBLISHED',
    data: error.value?.data,
    fatal: true,
  })
}
else if (error.value) {
  throw createError({
    status: 404,
    statusText: 'RV_NOT_FOUND',
    fatal: true,
  })
}

const destinationPath = rv.value?.Location
  ? [formatCountryCodeAsSlug(rv.value.Location.Country as COUNTRY_SHORTS), formatAsSlug(rv.value.Location.State ?? ''), formatAsSlug(rv.value.Location.City ?? ''), 'rv-rental'].filter(Boolean).join('/')
  : ''

const { data: destinationData } = await useRentals('/api/point-of-interest/destination-details/{languageCode}', {
  path: {
    languageCode: locale.value,
  },
  query: {
    Path: destinationPath,
    Faq: false,
    NearbyDistanceKM: 250,
    NearbyPageSize: 8,
    NearbyMinimumHubs: 2,
  },
  immediate: Boolean(destinationPath),
})

const {
  rvAliasName,
  rvAllowCrossBorderTravel,
  rvCleaningFee,
  rvCity,
  rvCountry,
  rvCurrency,
  rvDefaultPrice,
  rvDeliveryInstructions,
  rvFuelType,
  rvGuests,
  rvHasAccommodations,
  rvHasAddons,
  rvHasAmenity,
  rvHasDelivery,
  rvHasRangeDiscounts,
  rvId,
  rvInteriorFeatures,
  rvIsActivated,
  rvIsChargeMileage,
  rvIsFeatured,
  rvIsGenerator,
  rvIsInstantBook,
  rvIsPublished,
  rvIsStatic,
  rvIsSuperHostActive,
  rvLength,
  rvLocation,
  rvLocalizedRVType,
  rvMake,
  rvMainPhotoPath,
  rvModel,
  rvOwnerFirstName,
  rvOwnerId,
  rvOwnerScore,
  rvParkAmenities,
  rvPhotos,
  rvRentalType,
  rvRenterRating,
  rvReviews,
  rvRVNumber,
  rvRVDescription,
  rvRVName,
  rvRVType,
  rvSecurityDeposit,
  rvSlideouts,
  rvState,
  rvTour3DUrl,
  rvWeight,
  rvYear,
} = useRvDetails(rv)

const title = computed(() =>
  t(`metaTitle.${rvIsStatic.value ? 'rvCottage' : 'default'}`, {
    model:
          rvIsStatic.value
            ? t('common.rv')
            : formatRvLabel(
                rv.value?.Year,
                rv.value?.Make === RV_MANUFACTURERS_OTHER ? rv.value?.RVBrandOther : rv.value?.Make !== RV_MANUFACTURERS_NOT_APPLICABLE ? rv.value?.Make : undefined,
                rv.value?.Model,
              ),
    price: priceDisplay({
      value: rv.value?.DefaultPrice,
      countryCode: rv.value?.Location?.Country as COUNTRY_SHORTS,
      round: true,
    }),
  }),
)

const description = computed(() => {
  return t('metaDesc', {
    rvType: t(rv.value?.RVType ? `rvType.${rv.value.RVType}` : 'rvType.rvCottage'),
    city: rv.value?.Location?.City,
    state: lookupRegionToIsoCode(rv.value?.Location?.State),
    sleeps: rv.value?.Guests,
  })
})

const { getDynamicImage, presets } = useImage()
const preset = presets.LANDSCAPE_LG

const image = computed(() => {
  if (!rv.value?.Photos?.length) {
    return ''
  }

  return getDynamicImage({
    path: rvMainPhotoPath.value,
    preset,
  })
})

const rvMainPhoto = computed(() => {
  return getDynamicImage({
    path: rvMainPhotoPath.value,
    preset: presets.LANDSCAPE_SM,
    withFallback: true,
  })
})

const videoObject = computed(() => useVideoUrl({
  id: rv.value?.Id,
}))

const { data: video } = await useAsyncData('video', async () => {
  if (!videoObject.value) return null

  const checkVideoResponse = await fetch(videoObject.value.source, { method: 'HEAD' })

  if (checkVideoResponse.status === 200) {
    return videoObject.value
  }

  return null
})

const { buildNearbyLocation } = usePointOfInterest()
const nearbyDestinations = computed(() =>
  destinationData.value?.Nearby?.Results?.filter((location) => location.Indexable)?.slice(0, 4)?.map((location) => buildNearbyLocation('rv-rentals', location)) ?? [],
)

const viewAllCampgroundsLink = computed(() => {
  const pointOfInterest = destinationData.value?.RouteDetails?.PointOfInterest
  const destinationPath = destinationPathSegments(pointOfInterest?.CanonicalPaths?.[locale.value] ?? pointOfInterest?.CanonicalPath)

  if (!destinationPath.length) {
    return undefined
  }

  return localePath({ name: ROUTE_BASE_NAME.campgrounds_destination, params: { destinationPath } })
})

useSeoMeta({
  title: () => title.value,
  description: () => description.value,

  ogTitle: () => title.value,
  ogDescription: () => description.value,
  ogImage: () => image.value,

  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => image.value,
})

const { defineVehicle } = useAugmentedSchemaOrg()
const absoluteLocalePath = useAbsoluteLocalePath()
const absoluteRvUrl = computed(() => absoluteLocalePath(generateRvPath(rvAliasName.value ?? '')))
const BASE_URL = runtimeConfig.public.baseURL
const vehicleId = computed(() => `#rv-vehicle-${rvRVNumber.value}`)
const schemaReviews = rvReviews.value?.filter((review) => Boolean(review?.Content) && (review.OverallRating ?? 0) > 0)

useSchemaOrg([
  defineWebPage({
    mainEntity: { '@id': `${BASE_URL}/${vehicleId.value}` },
  }),
  {
    '@id': `${BASE_URL}/${vehicleId.value}`,
    ...defineVehicle({
      rvName: rvRVName.value!,
      rvType: rvRVType.value,
      rvRentalType: rvRentalType.value,
      rvMake: rvMake.value,
      rvModel: rvModel.value,
      rvCity: rvCity.value,
      rvRegion: rvState.value,
      rvCountry: rvCountry.value as COUNTRY_SHORTS,
      rvWeight: rvWeight.value,
      rvGuests: rvGuests.value,
      rvYear: rvYear.value,
      rvFuelType: rvFuelType.value,
      rvDefaultPrice: rvDefaultPrice.value,
      rvCleaningFee: rvCleaningFee.value,
      rvSecurityDeposit: rvSecurityDeposit.value,
      ownerFirstName: rvOwnerFirstName.value,
      rvDescription: rvRVDescription.value,
      rvIsChargeMileage: rvIsChargeMileage.value,
      rvIsGenerator: rvIsGenerator.value,
      rvLength: rvLength.value,
      rvSlideouts: rvSlideouts.value,
      url: absoluteRvUrl.value,
      images: (rvPhotos.value?.filter((photo) => photo.Path) ?? []).map((photo) => getDynamicImage({
        path: photo.Path,
        preset,
      }) ?? ''),
      aggregateRating: {
        ratingValue: rvRenterRating.value,
        reviewCount: schemaReviews?.length ?? 0,
      },
      reviews: schemaReviews?.map((review) => ({
        authorName: review.Reviewer?.FirstName ?? '',
        datePublished: review.CreatedAt,
        reviewBody: review.Content!,
        ratingValue: review.OverallRating,
      })),
    }),
  },
])

const {
  bookingEstimateData,
  bookingEstimateParams,
  hasDateRange,
  hasIncompleteDestination,
  resolvedRvUsage,
  fetchBookingEstimate,
} = useBookingEstimate()

await useAsyncData(
  () => fetchBookingEstimate(),
  {
    watch: [toReactive(bookingEstimateParams)],
    deep: true,
  },
)

const { fetchDeliveryCost, resetDeliveryCost } = useDeliveryCost()
const { getExperimentVariant, trackView: trackExperimentView } = useExperiment()
const deliveryCostParams = computed(() => ({
  lat: bookingEstimateParams.value.destination?.latitude,
  lng: bookingEstimateParams.value.destination?.longitude,
  country: bookingEstimateParams.value.destination?.country,
}))

const isDeliveryUsage = computed(() => resolvedRvUsage.value === 'Delivery' || resolvedRvUsage.value === 'StationaryAfterDelivery')

await useAsyncData(
  async () => {
    // Clear stale results on pickup
    if (!isDeliveryUsage.value) {
      resetDeliveryCost()

      return null
    }

    return fetchDeliveryCost({
      rvId: rvId.value,
      rvCountry: rvCountry.value,
      rvAllowCrossBorderTravel: rvAllowCrossBorderTravel.value,
      rvHasDelivery: rvHasDelivery.value,
      destination: deliveryCostParams.value,
      departureDate: bookingEstimateParams.value.dateStart,
    })
  },
  {
    watch: [
      isDeliveryUsage,
      () => bookingEstimateParams.value.destination?.latitude,
      () => bookingEstimateParams.value.destination?.longitude,
      () => hasDateRange.value ? bookingEstimateParams.value.dateStart : undefined,
    ],
  },
)

useHead({
  bodyAttrs: {
    class: {
      'pb-[120px]': isWebView.value,
    },
  },
})

onMounted(() => {
  trackView()
  trackExperimentView('rvz28229PriceSensitivityExperiment')
})

const { userId, userEmail } = useUser()

const { checkIfRvIsFavourited } = useFavouritesData()

const baseNightlyPrice = computed(() => {
  return bookingEstimateData.value?.Financial?.Nightly?.Base || rvDefaultPrice.value
})

const actualNightlyPrice = computed(() => {
  return bookingEstimateData.value?.Financial?.Nightly?.Discounted || baseNightlyPrice.value
})

const canShowDiscountTypeBadge = computed(() => {
  return getExperimentVariant('rvz28041DisplayDiscountTypeBadge') === 'variant'
})

const isSimplifiedRtbFlowVariant = computed(() => {
  return getExperimentVariant('rvz28661SimplifiedRtbFlow') === 'variant'
})

const hasMidweekdaysDiscount = computed(() => {
  return (bookingEstimateData.value?.Financial?.Discounts?.Midweekdays?.DiscountAmount ?? 0) > 0
})

const hasWeeklyDiscount = computed(() => {
  return (bookingEstimateData.value?.Financial?.Discounts?.Weekly?.DiscountAmount ?? 0) > 0
})

const hasMonthlyDiscount = computed(() => {
  return (bookingEstimateData.value?.Financial?.Discounts?.Monthly?.DiscountAmount ?? 0) > 0
})

const computedDiscountLabel = computed(() => {
  if (hasDateRange.value) {
    if (hasMonthlyDiscount.value) {
      return t('discount.monthly')
    }
    if (hasWeeklyDiscount.value) {
      return t('discount.weekly')
    }
    if (hasMidweekdaysDiscount.value) {
      return t('discount.midweek')
    }

    return ''
  }

  if (rvHasRangeDiscounts.value) {
    return t('discount.any')
  }

  return ''
})

const canShowRangeDiscount = ref(true)

const mosaicImagesRef = useTemplateRef('mosaicImagesRef')
const mosaicImagesIsVisible = useElementVisibility(mosaicImagesRef)

watch(mosaicImagesIsVisible, (isVisible) => {
  if (!isVisible) {
    canShowRangeDiscount.value = false
  }
})

const sessionCookie = useCookie(COOKIES.session)
const gidCookie = useCookie(COOKIES.gid)
const clickIdsCookie = useCookie<StoredClickIds>(COOKIES.clickIds)

const searchData = useSearchData()
const searchLocationParams = computed(() => getNormalizedSearchLocationParams(searchData.value.location))

function trackView() {
  const utmData = {
    clickIds: parseLiveClickIds(clickIdsCookie.value),
    sessionId: sessionCookie.value || gidCookie.value,
  }

  const eventData = {
    params: {
      rvId: rvId.value,
      email: userEmail.value,
      listingCountry: rvCountry.value?.toLowerCase() as Lowercase<COUNTRY_SHORTS>,
      listingRegion: rvLocation.value?.State ?? undefined,
      listingCity: rvLocation.value?.City ?? undefined,
      listingSleepingSpots: rv.value?.Guests,
      listingDeliveryOffered: rvHasDelivery.value,
      listingAmenities: rvInteriorFeatures.value,
      listingLocationAmenities: rvParkAmenities.value,
      listingName: rvRVName.value ?? undefined,
      listingPhotos: rvPhotos.value?.length,
      listingDescriptionChars: rvRVDescription.value?.length,
      listingBasePrice: rvDefaultPrice.value,
      cleaningPrice: rvCleaningFee.value,
      numReviews: rvReviews.value?.length ?? 0,
      starRating: rvRenterRating.value,
      isFeatured: rvIsFeatured.value,
      isInstantbook: rvIsInstantBook.value,
      isFavourite: checkIfRvIsFavourited(rvId.value),
      searchCity: searchLocationParams.value?.city,
      searchRegion: searchLocationParams.value?.region,
      searchCountry: searchLocationParams.value?.country,
      pageSource: getRvDetailsPageReferrer(),
      cta: getRvDetailsPageCta(),
      rentalPrice: bookingEstimateData.value?.Financial?.TotalRentalAmount,
      roadsidePrice: bookingEstimateData.value?.Financial?.TotalRoadsides,
      insurancePrice: bookingEstimateData.value?.Financial?.TotalInsurances,
      servicePrice: bookingEstimateData.value?.Financial?.TotalRenterServiceFee,
      totalPrice: bookingEstimateData.value?.Financial?.RenterTotal,
      currency: rvCurrency.value,
      rvType: rvRVType.value ?? 'MotorHome',
      rentalType: rvRentalType.value ?? 'MotorHome',
      isSuperhost: rvIsSuperHostActive.value,
      hasDeliveryNotes: Boolean(rvDeliveryInstructions.value),
      deliveryNotes: rvDeliveryInstructions.value ?? undefined,
      nightlyRate: actualNightlyPrice.value,
      smartNightlyRate: undefined, // This is only returned on snapshots
      fomoTag: undefined,
      rvUrl: absoluteRvUrl.value,
      brand: rvMake.value ?? 'unknown',
      ...(searchData.value.dates?.start
        ? { startDate: formatAsISOString(searchData.value.dates.start) }
        : {}),
      ...(searchData.value.dates?.end
        ? { endDate: formatAsISOString(searchData.value.dates.end) }
        : {}),
      isActivated: rvIsActivated.value,
      responseRate: rvOwnerScore.value ?? undefined,
      ...utmData,
    },
    additionalParams: {
      productId: rvId.value,
      rvUrl: absoluteRvUrl.value,
      brand: rvMake.value ?? 'unknown',
    },
  }

  trackListingViewed(eventData, runtimeConfig.public.googleAds.id)
}

/**
 * Gets the referrer from the rv details page.
 * This contains getRouteBaseName from instead of using the document.referrer, to keep thigns consistant.
 */
function getRvDetailsPageReferrer() {
  // Local storage doesn't work on Android apps
  if (!localStorage) return ''

  const referrer = window.localStorage.getItem(`rvDetailsPageCtaReferrer_${rvId.value}`)
  if (referrer) {
    // Remove the item once used, as we only want to track it for a single click
    window.localStorage.removeItem(`rvDetailsPageCtaReferrer_${rvId.value}`)
  }

  return referrer ?? ''
}

/**
 * Gets action the user performed to get to the RV Details page
 */
function getRvDetailsPageCta() {
  // Local storage doesn't work on Android apps
  if (!localStorage) return ''

  const cta = window.localStorage.getItem(`rvDetailsPageCta_${rvId.value}`)
  if (cta) {
    // Remove the item once used, as we only want to track it for a single click
    window.localStorage.removeItem(`rvDetailsPageCta_${rvId.value}`)
  }

  return cta ?? ''
}

const detailsSection = computed(() => {
  const details: DETAIL_TYPES[] = []

  if (rvHasDelivery.value) {
    details.push(DETAIL_TYPES.Delivery)
  }

  if (rvHasAmenity.value('Petfriendly')) {
    details.push(DETAIL_TYPES.Petfriendly)
  }

  if (rv.value?.IsSuperHostActive) {
    details.push(DETAIL_TYPES.Superhost)
  }

  return details
})

const accommodationsOptions = computed(() => {
  return Object.values(BED_TYPES).reduce(
    (acc: Record<BED_TYPES, number>, bed: BED_TYPES) => {
      if (rv.value?.[bed]) acc[bed] = rv.value?.[bed] as number
      return acc
    },
    {} as Record<BED_TYPES, number>,
  )
})

const mosaicImages = computed(() => {
  const media: SwipeContent[] = rvPhotos.value?.filter((photo) => photo.Path).map((photo) => ({
    type: 'image',
    path: photo.Path ?? '',
    src: getDynamicImage({
      path: photo.Path,
      preset,
    }) ?? '',
    description: photo.Description ?? '',
    width: preset.width,
    height: preset.height,
  })) ?? []

  if (video.value) {
    // Ensure video.value has the required 'type' property for SwipeVideo
    const videoContent: SwipeContent = { type: 'video', ...video.value }
    if (media.length > 3) {
      media.splice(2, 0, videoContent)
    }
    else {
      media.push(videoContent)
    }
  }

  return media
})

const isRvOwner = computed(() => {
  return userId.value === rvOwnerId.value
})

const estimateListingForm = useTemplateRef('estimateListingForm')
const expandedDetails = ref(false)

async function expandDetails(field?: 'calendar' | 'destination') {
  expandedDetails.value = true

  if (field === 'calendar') {
    await nextTick(() => {
      estimateListingForm.value?.openCalendar()
    })
  }
  else if (field === 'destination') {
    await nextTick(() => {
      estimateListingForm.value?.openDestination()
    })
  }
}

function closeDetails() {
  expandedDetails.value = false
}

function toggleDetails(visible: boolean) {
  expandedDetails.value = visible
}
</script>

<i18n src="~~/i18n/locales/common/booking/request.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "metaTitle": {
      "default": "Rent my {model} from {price}/night",
      "rvCottage": "Rent my RV Cottage from {price}/night"
    },
    "metaDesc": "Top-rated {rvType} rental in {city}, {state}. Sleeps {sleeps} people. Weekly & monthly discounts. Best price guarantee! Check availability now.",
    "discount": {
      "midweek": "This host is offering a midweek discount",
      "weekly": "This host is offering a weekly discount",
      "monthly": "This host is offering a monthly discount",
      "any": "This host offers discounts"
    },
    "in": "in"
  },
  "fr": {
    "metaTitle": {
      "default": "Louez mon {model} pour {price}/nuit",
      "rvCottage": "Louez mon Chalet VR pour {price}/nuit"
    },
    "metaDesc": "{rvType} à louer à {city}, {state}. Couche {sleeps} personnes. Rabais hebdomadaire et mensuel. Meilleur prix garantie. Voir les disponibilités aujourd’hui.",
    "discount": {
      "midweek": "Cet hôte offre un rabais en semaine",
      "weekly": "Cet hôte offre un rabais hebdomadaire",
      "monthly": "Cet hôte offre un rabais mensuel",
      "any": "Cet hôte offre des rabais"
    },
    "in": "·"
  }
}
</i18n>
